import type { DialogComponentProps } from '@brightdrop/bd-ui';
import { AlertBanner } from '@brightdrop/bd-ui';
import type {
  MRT_Row,
  MRT_RowSelectionState,
  MRT_TableOptions,
  TableToolbarProps,
} from '@brightdrop/table-ui';
import { Table, TableFilterDisplayMode } from '@brightdrop/table-ui';
import { useMediaQuery, useTheme } from '@mui/material';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import BDFilterModal, {
  BDFilterModalType,
} from '~/common/components/BDFilter/BDFilterModal';
import { useLocalizedIntegerCount } from '~/common/localization/useLocalizedFormatters';
import { getFleetFilterConfig } from '~/features/catalog/shared/components/eligibleVehiclesView/filter/eligibleVehiclesFilter.config';
import {
  ELIGIBLE_VEHICLES_FILTER_EMPTY_STATE,
  type EligibleVehiclesFilterCriteria,
} from '~/features/catalog/shared/components/eligibleVehiclesView/filter/eligibleVehiclesFilter.model';
import { useCatalogFleets } from '~/features/catalog/shared/hooks/useCatalogFleets';
import { useNavigateToAsset } from '~/features/catalog/shared/hooks/useNavigateToAsset';
import {
  type EnrollmentRow,
  getEnrollmentTableColumns,
  IS_SORT_SUPPORTED,
} from '~/features/integrations/manage/enrollmentTable/enrollmentTable.columns';

import {
  ENROLLMENT_TABLE_PAGE_SIZE,
  ENROLLMENT_VIEW_STATUSES,
  type EnrollmentStatus,
} from '~/features/integrations/manage/enrollmentTable/enrollmentTable.constants';
import { useEnrollmentViewRows } from '~/features/integrations/manage/enrollmentTable/useEnrollmentViewRows';
import type {
  ManageSectionProps,
  ManageTab,
  PartnerConfig,
} from '~/features/integrations/partners/types';

const TEST_ID_PREFIX = 'enrollment-table';
const DEFAULT_FLEET_FILTER: EligibleVehiclesFilterCriteria = {
  ...ELIGIBLE_VEHICLES_FILTER_EMPTY_STATE,
};
// The table's loading skeleton passes placeholder rows that carry no vehicle.
const getRowId = (row: EnrollmentRow, index: number) =>
  row.vehicle?.vin ?? `loading-${index}`;

type EnrollmentTableBodyProps = {
  partner: PartnerConfig;
  tab: ManageTab;
  enrollmentStatuses: EnrollmentStatus[];
};

const EnrollmentTableBody = ({
  partner,
  tab,
  enrollmentStatuses,
}: EnrollmentTableBodyProps) => {
  const { t } = useTranslation(['integrations', 'common', 'catalog', 'asset']);
  const [search, setSearch] = useState('');
  const [fleetIds, setFleetIds] = useState<string[]>([]);
  const [rowSelection, setRowSelection] = useState<MRT_RowSelectionState>({});
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: ENROLLMENT_TABLE_PAGE_SIZE,
  });
  const { rows, total, status, retry } = useEnrollmentViewRows({
    providerCode: partner.providerCode,
    enrollmentStatuses,
    fleetIds,
    searchTerm: search.trim(),
    pageIndex: pagination.pageIndex,
    pageSize: pagination.pageSize,
  });
  const formatCount = useLocalizedIntegerCount();
  const fleets = useCatalogFleets();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isFetching = status === 'loading';
  const { navigateToAsset } = useNavigateToAsset();

  // The VIN doubles as the asset id on the vehicle details route.
  const openVehicle = (row: MRT_Row<EnrollmentRow>) => {
    const vin = row.original.vehicle?.vin;
    if (vin) navigateToAsset(vin);
  };

  const handleSearch = (input: string) => {
    setSearch(input);
    setPagination((previous) => ({ ...previous, pageIndex: 0 }));
  };
  const handleFleetFilterChange = (nextFleetIds: string[]) => {
    setFleetIds(nextFleetIds);
    setPagination((previous) => ({ ...previous, pageIndex: 0 }));
  };

  const FleetFilterModal = useMemo(
    () => (props: DialogComponentProps<EligibleVehiclesFilterCriteria>) => (
      <BDFilterModal
        {...props}
        filterOptions={getFleetFilterConfig(fleets)}
        defaultState={DEFAULT_FLEET_FILTER}
        type={BDFilterModalType.DROPDOWN}
      />
    ),
    [fleets]
  );

  const filterConfig = {
    component: FleetFilterModal,
    entity: { ...DEFAULT_FLEET_FILTER, fleetIds },
    defaultState: DEFAULT_FLEET_FILTER,
    id: `${TEST_ID_PREFIX}-fleet-filter`,
    displayMode: isMobile
      ? TableFilterDisplayMode.MODAL
      : TableFilterDisplayMode.POPOVER,
    saveFilterOptions: (options: Partial<EligibleVehiclesFilterCriteria>) =>
      handleFleetFilterChange(options.fleetIds ?? []),
    getChipLabel: (_name: unknown, value: unknown) =>
      fleets.find((fleet) => fleet.fleetId === value)?.name ?? String(value),
  } as unknown as TableToolbarProps<unknown>['filterConfig'];

  const tableProps: MRT_TableOptions<EnrollmentRow> = {
    columns: getEnrollmentTableColumns(tab.columns),
    data: rows,
    getRowId,
    rowCount: total,
    enableRowSelection: true,
    enableSelectAll: true,
    enablePagination: true,
    enableSorting: IS_SORT_SUPPORTED,
    enableStickyHeader: true,
    onPaginationChange: setPagination,
    onRowSelectionChange: setRowSelection,
    muiTableBodyRowProps: ({ row }) => ({
      onClick: () => openVehicle(row),
      style: { cursor: 'pointer' },
    }),
    state: { pagination, rowSelection, isLoading: isFetching },
  };

  return (
    <div data-testid={`${TEST_ID_PREFIX}-${tab.id}`}>
      {status === 'error' && (
        <AlertBanner
          variant="error"
          open
          title={t('common:errors.unableToLoad')}
          actionLabel={t('common:retry')}
          onAction={retry}
          testId={`${TEST_ID_PREFIX}-load-error`}
        />
      )}
      <Table
        tablePropsObj={tableProps}
        isPaginationDisabled={isFetching}
        onRowClick={(_event, row) => openVehicle(row)}
        enableMobileViewLayout
        renderAccordionMobileView
        tableToolbarConfig={{
          countLabelConfig: {
            translationKey: 'catalog:vehiclesTable.countVehicles',
            variables: { count: total, formattedCount: formatCount(total) },
            dataTestId: `${TEST_ID_PREFIX}-count`,
          },
          searchBarConfig: {
            onSearch: (_type, input) => handleSearch(input),
            onClear: () => handleSearch(''),
            searchInput: search,
            withoutSearchTypeSelector: true,
            hideSearchByPrefix: true,
            dataTestId: `${TEST_ID_PREFIX}-search`,
          },
          filterConfig,
        }}
      />
    </div>
  );
};

/**
 * One vehicle table for every status tab. The tab's id picks the statuses requested and its
 * `columns` pick what follows the vehicle cell; nothing here depends on which partner it is.
 */
const EnrollmentTable = ({
  partner,
  tab,
}: ManageSectionProps): JSX.Element | null => {
  const enrollmentStatuses = tab && ENROLLMENT_VIEW_STATUSES[tab.id];
  if (!tab || !enrollmentStatuses) return null;
  // Keyed by tab so search, filter, page, and selection never carry over between tabs.
  return (
    <EnrollmentTableBody
      key={tab.id}
      partner={partner}
      tab={tab}
      enrollmentStatuses={enrollmentStatuses}
    />
  );
};

export default Object.assign(EnrollmentTable, {
  sectionId: 'enrollment-table',
});
